import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField as MuiTextField,
  Button,
  Box,
  Breadcrumbs,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
  LinearProgress,
  FormLabel,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import { useParams, NavLink } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  changeUserPassword,
  fetchUserById,
  InvoiceDeliveryMethod,
  updateUser,
  User,
} from "../../redux/slices/user";
import useAuth from "../../hooks/useAuth";
import styled from "@emotion/styled";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const userSchema = yup.object().shape({
  firstName: yup.string().required("Etunimi on pakollinen"),
  lastName: yup.string().required("Sukunimi on pakollinen"),
  email: yup
    .string()
    .email("Virheellinen sähköposti")
    .required("Sähköposti vaaditaan"),
  phone: yup
    .string()
    .matches(/^[0-9]{10,}$/, "Anna voimassa oleva puhelinnumero"),
  street: yup.string().required("Katuosoite on pakollinen"),
  city: yup.string().required("Kaupunki tai kunta vaaditaan"),
  postalCode: yup
    .string()
    .matches(/^\d{5}$/, "Postinumero tulee olla 5 numeroa"),
  invoiceDeliveryMethod: yup
    .mixed<InvoiceDeliveryMethod>()
    .oneOf([InvoiceDeliveryMethod.EMAIL, InvoiceDeliveryMethod.POST]),
});

const passwordSchema = yup.object().shape({
  currentPassword: yup.string().required("Nykyinen salasana vaaditaan"),
  newPassword: yup
    .string()
    .required("Uusi salasana vaaditaan")
    .min(8, "Vähintään 8 merkkiä")
    .matches(/[A-Z]/, "Vähintään yksi iso kirjain")
    .matches(/[a-z]/, "Vähintään yksi pieni kirjain")
    .matches(/\d/, "Vähintään yksi numero")
    .matches(/[@$!%*?#&]/, "Vähintään yksi erikoismerkki"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Salasanat eivät täsmää")
    .required("Vahvista salasana"),
});

const SettingsPage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const user = useAppSelector((state) => state.user.user);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  //const duration = 6000;
  //const [snackbarMessage, setSnackbarMessage] = useState("");
  //const [snackbarSeverity, setSnackbarSeverity] = useState<
  //  "success" | "error" | "warning" | "info"
  //>("success");
  //const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (id) dispatch(fetchUserById(id));
  }, [dispatch, id]);

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    if (snackbarOpen) {
      const interval = 100;
      const decrement = 100 / (duration / interval);

      const timer = setInterval(() => {
        setProgress((old) => {
          const updated = Math.max(old - decrement, 0);
          if (updated === 0) {
            clearInterval(timer);
            setSnackbarOpen(false);
          }
          return updated;
        });
      }, interval);

      return () => clearInterval(timer);
    }
  }, [snackbarOpen]);

  if (!user || !currentUser || user.id !== currentUser.id) {
    return (
      <>
        <Helmet title="Asetukset" />
        <Typography variant="h3">Asetukset</Typography>
        <Breadcrumbs sx={{ mt: 2 }}>
          <Link component={NavLink} to="/">
            Etusivu
          </Link>
          <Typography>Asetukset</Typography>
        </Breadcrumbs>
        <Divider sx={{ my: 4 }} />
        <Typography color="error">
          Sinulla ei ole oikeuksia nähdä tätä profiilia.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Helmet title="Asetukset" />
      <Typography variant="h3">Asetukset</Typography>
      <Breadcrumbs sx={{ mt: 2 }}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Asetukset</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 4 }} />

      {/* Käyttäjäprofiilin lomake */}
      <Formik
        initialValues={{
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          email: user.email ?? "",
          phone: user.phone ?? "",
          street: user.address?.street ?? "",
          city: user.address?.city ?? "",
          postalCode: user.address?.postalCode ?? "",
          invoiceDeliveryMethod:
            user.invoiceDeliveryMethod ?? InvoiceDeliveryMethod.EMAIL,
        }}
        validationSchema={userSchema}
        onSubmit={(values) => {
          dispatch(updateUser({ id: user.id, data: values }));
          setSnackbarMessage("Asetukset tallennettu!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
          setProgress(100);
        }}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
        }) => (
          <form onSubmit={handleSubmit}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6">Käyttäjäprofiili</Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={6}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="firstName"
                      label="Etunimi"
                      fullWidth
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="lastName"
                      label="Sukunimi"
                      fullWidth
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="email"
                      label="Sähköposti"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      disabled
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="phone"
                      label="Puhelinnumero"
                      fullWidth
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="street"
                      label="Katuosoite"
                      fullWidth
                      value={values.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.street && errors.street)}
                      helperText={touched.street && errors.street}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="postalCode"
                      label="Postinumero"
                      fullWidth
                      value={values.postalCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      helperText={touched.postalCode && errors.postalCode}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="city"
                      label="Kaupunki / Kunta"
                      fullWidth
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      my={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormLabel component="legend">Laskutustapa</FormLabel>
                    <RadioGroup
                      name="invoiceDeliveryMethod"
                      row
                      value={values.invoiceDeliveryMethod}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="EMAIL"
                        control={<Radio />}
                        label="Sähköposti"
                      />
                      <FormControlLabel
                        value="POST"
                        control={<Radio />}
                        label="Posti"
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4 }}>
                  <Button variant="contained" type="submit">
                    Tallenna
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </form>
        )}
      </Formik>

      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={passwordSchema}
        onSubmit={async ({ currentPassword, newPassword }, actions) => {
          try {
            const result = await dispatch(
              changeUserPassword({ id, currentPassword, newPassword })
            ).unwrap();

            if (result === true || result?.success === true) {
              actions.resetForm();
              setProgress(100);
              setSnackbarMessage("Salasana vaihdettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              throw new Error("UNKNOWN_ERROR");
            }

            setOpenSnackbar(true);
          } catch (error) {
            setProgress(100);
            setSnackbarMessage("Tapahtui virhe salasanan vaihtamisessa.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
          }
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
        }) => {
          const passwordFields: (keyof typeof values)[] = [
            "currentPassword",
            "newPassword",
            "confirmPassword",
          ];

          return (
            <form onSubmit={handleSubmit}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Vaihda salasana</Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    {passwordFields.map((field) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          name={field}
                          label={
                            field === "currentPassword"
                              ? "Nykyinen salasana"
                              : field === "newPassword"
                              ? "Uusi salasana"
                              : "Vahvista uusi salasana"
                          }
                          type="password"
                          value={values[field]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched[field] && errors[field])}
                          helperText={touched[field] && errors[field]}
                          autoComplete="off"
                          my={2}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" type="submit">
                      Vaihda salasana
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </form>
          );
        }}
      </Formik>
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

export default SettingsPage;
